/**
 * Website & Booking Engine — Universal integration hub.
 *
 * One page per hotel with 6 tabs:
 *   • Përmbledhje (Overview)     — status, hosted URL, last activity, KPIs
 *   • Linku i Rezervimit         — hosted booking URL + copy + QR
 *   • Widget                     — visual configurator + copy-paste embed code
 *   • WordPress                  — download plugin ZIP + shortcode help
 *   • API Access                 — publishable + secret keys, allowed origins
 *   • Analytics                  — 30-day funnel
 *
 * The heavy lifting (search, availability, pricing) happens on the backend
 * booking engine — this page just surfaces the integration surface area.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import {
    Globe2, Copy, QrCode, Code2, Link as LinkIcon, KeyRound,
    ShieldCheck, Trash2, Plus, RefreshCw, Download, TerminalSquare,
    AlertTriangle, ExternalLink, CheckCircle2, BarChart3,
    Sparkles, CreditCard, XCircle, Info,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { key: "overview",  icon: Globe2 },
    { key: "link",      icon: LinkIcon },
    { key: "widget",    icon: Code2 },
    { key: "wordpress", icon: TerminalSquare },
    { key: "api",       icon: KeyRound },
    { key: "domains",   icon: Globe2 },
    { key: "branding",  icon: Sparkles },
    { key: "payments",  icon: CreditCard },
    { key: "analytics", icon: BarChart3 },
];

export default function IntegrationHub() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [tab, setTab] = useState("overview");
    const [cfg, setCfg] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const hotelId = user?.hotel_id;

    const load = async () => {
        if (!hotelId) return;
        try {
            const { data } = await api.get(`/properties/${hotelId}/integration/config`);
            setCfg(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const loadAnalytics = async () => {
        if (!hotelId) return;
        try {
            const { data } = await api.get(`/properties/${hotelId}/integration/analytics`);
            setAnalytics(data);
        } catch (e) { /* silent */ }
    };
    useEffect(() => { load(); loadAnalytics(); /* eslint-disable-next-line */ }, [hotelId]);

    if (!cfg) return (
        <>
            <Topbar title={t("integration.title", "Website & Booking Engine")} />
            <div className="p-8 text-sm text-muted-foreground">{t("common.loading_ellipsis")}</div>
        </>
    );

    return (
        <>
            <Topbar
                title={t("integration.title", "Website & Booking Engine")}
                subtitle={cfg.hotel.name + " · " + cfg.public_property_id}
                actions={
                    <a href={cfg.hosted_booking_url} target="_blank" rel="noopener noreferrer"
                       className="akm-btn-ghost text-[11px]" data-testid="integration-open-hosted">
                        <ExternalLink size={12} /> {t("integration.open_hosted", "Open hosted page")}
                    </a>
                }
            />

            <div className="flex-1 overflow-auto p-4 md:p-6" data-testid="integration-hub">
                {/* Tab bar */}
                <div className="mb-4 flex flex-wrap gap-1 border-b border-border">
                    {TABS.map(({ key, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            data-testid={`integration-tab-${key}`}
                            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                tab === key ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            <Icon size={13} />
                            {t(`integration.tabs.${key}`, defaultLabel(key))}
                        </button>
                    ))}
                </div>

                {tab === "overview"  && <OverviewTab cfg={cfg} analytics={analytics} onSwitch={setTab} />}
                {tab === "link"      && <BookingLinkTab cfg={cfg} />}
                {tab === "widget"    && <WidgetTab cfg={cfg} />}
                {tab === "wordpress" && <WordPressTab cfg={cfg} />}
                {tab === "api"       && <ApiAccessTab cfg={cfg} reload={load} hotelId={hotelId} />}
                {tab === "domains"   && <DomainsTab cfg={cfg} reload={load} hotelId={hotelId} />}
                {tab === "branding"  && <BrandingTab cfg={cfg} reload={load} hotelId={hotelId} />}
                {tab === "payments"  && <PaymentsTab cfg={cfg} reload={load} hotelId={hotelId} />}
                {tab === "analytics" && <AnalyticsTab analytics={analytics} onRefresh={loadAnalytics} />}
            </div>
        </>
    );
}

function defaultLabel(k) {
    return {
        overview: "Përmbledhje",
        link: "Linku i Rezervimit",
        widget: "Widget",
        wordpress: "WordPress",
        api: "API Access",
        domains: "Domains",
        branding: "Branding",
        payments: "Stripe",
        analytics: "Analytics",
    }[k] || k;
}

// ─────────────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────────────
function OverviewTab({ cfg, analytics, onSwitch }) {
    const { t } = useTranslation();
    const status = (label, ok, hint) => (
        <div className="akm-card p-3 flex items-start gap-3">
            <div className={`mt-0.5 h-2.5 w-2.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold">{label}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
            </div>
        </div>
    );
    return (
        <div className="space-y-4" data-testid="integration-overview">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                {status(t("integration.status.booking", "Motori i rezervimeve"), true, cfg.hosted_booking_url)}
                {status(t("integration.status.widget", "Widget universal"), true, "Gati për ngjitje në çdo faqe web")}
                {status(t("integration.status.api", "Public API v1"), true, `Rate limit: ${cfg.rate_limit_per_min} rpm`)}
                {status(t("integration.status.cors", "CORS"), cfg.allowed_origins.length > 0, cfg.allowed_origins.length ? `${cfg.allowed_origins.length} origins` : t("integration.cors_empty", "Asnjë origin — çdo faqe pranohet"))}
            </div>

            <div className="akm-card p-4">
                <div className="flex items-center gap-2 text-[13px] font-semibold"><Globe2 size={13} /> {t("integration.your_endpoints", "URL-të dhe çelësat")}</div>
                <dl className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                    <KVRow k={t("integration.public_property_id", "Public Property ID")} v={cfg.public_property_id} />
                    <KVRow k={t("integration.publishable_key", "Publishable key")} v={cfg.publishable_key} secret />
                    <KVRow k={t("integration.hosted_url", "Hosted booking URL")} v={cfg.hosted_booking_url} />
                    <KVRow k={t("integration.rate_limit", "Rate limit")} v={`${cfg.rate_limit_per_min} req/min`} />
                </dl>
            </div>

            {analytics && (
                <div className="akm-card p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[13px] font-semibold">{t("integration.last_30d", "Aktiviteti — 30 ditët e fundit")}</div>
                        <button onClick={() => onSwitch("analytics")} className="text-[11px] text-[#ff7b02] hover:underline">{t("integration.view_details", "Detajet →")}</button>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-6">
                        <MiniKPI label={t("integration.k.widget", "Widget loads")} v={analytics.widget_loads} />
                        <MiniKPI label={t("integration.k.search", "Kërkime")} v={analytics.availability} />
                        <MiniKPI label={t("integration.k.holds", "Hold-e")} v={analytics.holds} />
                        <MiniKPI label={t("integration.k.checkouts", "Checkout")} v={analytics.checkouts} />
                        <MiniKPI label={t("integration.k.reservations", "Rezervime")} v={analytics.reservations} />
                        <MiniKPI label={t("integration.k.config", "Config calls")} v={analytics.config_requests} />
                    </div>
                </div>
            )}
        </div>
    );
}

function MiniKPI({ label, v }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold tabular-nums">{v || 0}</div>
        </div>
    );
}

function KVRow({ k, v, secret = false }) {
    const [reveal, setReveal] = useState(false);
    const display = secret && !reveal ? maskKey(v) : v;
    return (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-[12px]">
            <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                <div className="mt-0.5 font-mono">{display}</div>
            </div>
            <div className="flex items-center gap-1">
                {secret && (
                    <button onClick={() => setReveal((r) => !r)} className="akm-btn-ghost text-[10px]" data-testid="kv-reveal">
                        {reveal ? "Fshih" : "Trego"}
                    </button>
                )}
                <CopyButton value={v} />
            </div>
        </div>
    );
}

function maskKey(v) {
    if (!v) return "";
    if (v.length < 16) return v;
    return v.slice(0, 12) + "…" + "•".repeat(24);
}

function CopyButton({ value, label }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch { toast.error("Kopjimi dështoi"); }
    };
    return (
        <button onClick={copy} className="akm-btn-ghost text-[10px]" data-testid="copy-btn">
            {copied ? <CheckCircle2 size={11} className="text-emerald-600" /> : <Copy size={11} />}
            {label && <span className="ml-1">{label}</span>}
        </button>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Booking link
// ─────────────────────────────────────────────────────────────────────────
function BookingLinkTab({ cfg }) {
    const { t } = useTranslation();
    const qrUrl = useMemo(() => (
        `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(cfg.hosted_booking_url)}&margin=8`
    ), [cfg.hosted_booking_url]);
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="integration-link">
            <div className="lg:col-span-2 space-y-4">
                <div className="akm-card p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("integration.hosted_url", "Hosted booking URL")}</div>
                    <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-muted/30 p-2.5 font-mono text-[12px]">
                        <span className="flex-1 truncate" data-testid="hosted-url">{cfg.hosted_booking_url}</span>
                        <CopyButton value={cfg.hosted_booking_url} label={t("integration.copy", "Kopjo")} />
                        <a href={cfg.hosted_booking_url} target="_blank" rel="noopener noreferrer" className="akm-btn-ghost text-[10px]" data-testid="open-hosted">
                            <ExternalLink size={11} /> {t("integration.open", "Hap")}
                        </a>
                    </div>
                </div>
                <div className="akm-card p-4">
                    <div className="text-[13px] font-semibold">{t("integration.share.title", "Ndaje kudo në 1 klikim")}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-muted-foreground">
                        <div>📸 <strong>Instagram bio</strong> — ngjit URL-në në seksionin &ldquo;Website&rdquo;</div>
                        <div>🗨️ <strong>WhatsApp</strong> — ndaje si status ose me klientët</div>
                        <div>📍 <strong>Google Business</strong> — vendos si buton &ldquo;Book&rdquo; në profil</div>
                        <div>📧 <strong>Email marketing</strong> — CTA i drejtpërdrejtë</div>
                        <div>🎫 <strong>Broshura & QR code</strong> — printo dhe ngjit</div>
                        <div>🔗 <strong>Facebook / TikTok</strong> — link në bio ose stori</div>
                    </div>
                </div>
            </div>
            <div className="akm-card p-4 flex flex-col items-center" data-testid="qr-panel">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <QrCode size={11} className="mr-1 inline" /> {t("integration.qr_title", "Kodi QR i rezervimeve")}
                </div>
                <img src={qrUrl} alt="QR" className="my-3 rounded-lg border border-border" width="240" height="240" />
                <a href={qrUrl + "&download=1"} download="akomoda-booking-qr.png" className="akm-btn-ghost text-[11px]">
                    <Download size={11} /> {t("integration.qr_download", "Shkarko PNG")}
                </a>
                <div className="mt-2 text-center text-[10px] text-muted-foreground max-w-[220px]">
                    {t("integration.qr_hint", "Printo në recepsion, në karta biznesi ose në menu që klientët të skanojnë dhe rezervojnë direkt.")}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Widget configurator
// ─────────────────────────────────────────────────────────────────────────
function WidgetTab({ cfg }) {
    const { t } = useTranslation();
    const [opts, setOpts] = useState({
        layout: "horizontal",
        mode: "redirect",
        theme: "light",
        language: "sq",
        primary_color: "#ff7b02",
        show_promo: true,
        show_children: true,
        show_rooms: true,
    });
    const scriptSrc = window.location.origin + "/api/v1/public/widget.js";
    const attrs = [
        `src="${scriptSrc}"`,
        `data-property="${cfg.public_property_id}"`,
        `data-key="${cfg.publishable_key}"`,
        `data-language="${opts.language}"`,
        `data-mode="${opts.mode}"`,
        `data-layout="${opts.layout}"`,
        `data-theme="${opts.theme}"`,
        `data-primary-color="${opts.primary_color}"`,
        `data-show-promo="${opts.show_promo}"`,
        `data-show-children="${opts.show_children}"`,
        `data-show-rooms="${opts.show_rooms}"`,
    ];
    const embed = `<div id="akomoda-booking-widget"></div>\n<script\n  ${attrs.join("\n  ")}\n  async>\n</script>`;
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="integration-widget">
            <div className="akm-card p-4 space-y-3 lg:col-span-1">
                <div className="text-[13px] font-semibold">{t("integration.widget.config", "Konfigurues vizual")}</div>
                <FormRow label={t("integration.widget.layout", "Layout")}>
                    <select className="akm-input" value={opts.layout} onChange={(e) => setOpts({ ...opts, layout: e.target.value })}
                            data-testid="widget-layout">
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertikal</option>
                        <option value="floating">Floating button</option>
                    </select>
                </FormRow>
                <FormRow label={t("integration.widget.mode", "Mode")}>
                    <select className="akm-input" value={opts.mode} onChange={(e) => setOpts({ ...opts, mode: e.target.value })}
                            data-testid="widget-mode">
                        <option value="redirect">Redirect (te hosted booking)</option>
                        <option value="overlay">Overlay (modal fullscreen)</option>
                    </select>
                </FormRow>
                <FormRow label={t("integration.widget.language", "Gjuha")}>
                    <select className="akm-input" value={opts.language} onChange={(e) => setOpts({ ...opts, language: e.target.value })}>
                        <option value="sq">Shqip</option>
                        <option value="en">English</option>
                    </select>
                </FormRow>
                <FormRow label={t("integration.widget.theme", "Tema")}>
                    <select className="akm-input" value={opts.theme} onChange={(e) => setOpts({ ...opts, theme: e.target.value })}>
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                    </select>
                </FormRow>
                <FormRow label={t("integration.widget.color", "Ngjyra primare")}>
                    <input type="color" value={opts.primary_color}
                           onChange={(e) => setOpts({ ...opts, primary_color: e.target.value })}
                           className="h-8 w-16 rounded border border-border" data-testid="widget-color" />
                </FormRow>
                <div className="space-y-1 border-t border-border pt-2">
                    {[
                        ["show_promo", t("integration.widget.show_promo", "Shfaq kodin promocional")],
                        ["show_children", t("integration.widget.show_children", "Shfaq numrin e fëmijëve")],
                        ["show_rooms", t("integration.widget.show_rooms", "Shfaq numrin e dhomave")],
                    ].map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2 text-[12px]">
                            <input type="checkbox" checked={opts[k]}
                                   onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })}
                                   data-testid={`widget-${k}`} />
                            {label}
                        </label>
                    ))}
                </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
                <div className="akm-card p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[13px] font-semibold">{t("integration.widget.embed_code", "Kodi për ngjitje")}</div>
                        <CopyButton value={embed} label={t("integration.copy", "Kopjo")} />
                    </div>
                    <pre className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]"
                         data-testid="widget-embed-code">{embed}</pre>
                    <div className="mt-2 text-[11px] text-muted-foreground">
                        {t("integration.widget.instructions", "Ngjit këtë kod në çdo faqe HTML/WordPress/Webflow ku dëshiron formularin e rezervimit.")}
                    </div>
                </div>
                <div className="akm-card p-4">
                    <div className="text-[13px] font-semibold mb-2">{t("integration.widget.preview", "Parapamja e drejtpërdrejtë")}</div>
                    <WidgetPreview cfg={cfg} opts={opts} />
                </div>
            </div>
        </div>
    );
}

function WidgetPreview({ cfg, opts }) {
    const containerRef = useMemo(() => `akomoda-widget-preview-${Math.random().toString(36).slice(2, 8)}`, []);
    useEffect(() => {
        const host = document.getElementById(containerRef);
        if (!host) return;
        host.innerHTML = "";
        const s = document.createElement("script");
        s.src = window.location.origin + `/api/v1/public/widget.js?_=${Date.now()}`;
        s.async = true;
        s.setAttribute("data-property", cfg.public_property_id);
        s.setAttribute("data-key", cfg.publishable_key);
        s.setAttribute("data-language", opts.language);
        s.setAttribute("data-mode", opts.mode);
        s.setAttribute("data-layout", opts.layout);
        s.setAttribute("data-theme", opts.theme);
        s.setAttribute("data-primary-color", opts.primary_color);
        s.setAttribute("data-show-promo", String(opts.show_promo));
        s.setAttribute("data-show-children", String(opts.show_children));
        s.setAttribute("data-show-rooms", String(opts.show_rooms));
        s.setAttribute("data-target", `#${containerRef}`);
        host.appendChild(s);
        return () => { host.innerHTML = ""; };
    }, [cfg.public_property_id, cfg.publishable_key, opts.layout, opts.mode, opts.theme, opts.language, opts.primary_color, opts.show_promo, opts.show_children, opts.show_rooms, containerRef]);
    return <div id={containerRef} data-testid="widget-preview" className="min-h-[140px]" />;
}

// ─────────────────────────────────────────────────────────────────────────
// WordPress
// ─────────────────────────────────────────────────────────────────────────
function WordPressTab({ cfg }) {
    const { t } = useTranslation();
    const downloadUrl = `${window.location.origin}/api/v1/public/wordpress-plugin.zip?property=${encodeURIComponent(cfg.public_property_id)}`;
    const shortcodeRedirect = `[akomoda_booking_form property="${cfg.public_property_id}" mode="redirect"]`;
    const shortcodeWidget = `[akomoda_booking_form property="${cfg.public_property_id}" mode="widget"]`;
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="integration-wordpress">
            <div className="akm-card p-4 space-y-3">
                <div className="text-[13px] font-semibold">{t("integration.wp.download_title", "Shkarko plugin-in për WordPress")}</div>
                <p className="text-[12px] text-muted-foreground">
                    {t("integration.wp.hint", "Plugin i dedikuar për WordPress që përfshin një shortcode dhe një Gutenberg block. Property ID është parapopulluar.")}
                </p>
                <a href={downloadUrl} download="akomoda-booking-engine.zip"
                   className="akm-btn-primary inline-flex items-center gap-1.5"
                   data-testid="wp-download">
                    <Download size={13} /> akomoda-booking-engine.zip
                </a>
                <ol className="mt-3 space-y-1 text-[12px] text-muted-foreground">
                    <li>1. {t("integration.wp.step1", "WordPress → Plugins → Add new → Upload plugin.")}</li>
                    <li>2. {t("integration.wp.step2", "Aktivizo plugin-in.")}</li>
                    <li>3. {t("integration.wp.step3", "Përdor shortcode-in ose Gutenberg block-un në çdo faqe.")}</li>
                </ol>
            </div>
            <div className="akm-card p-4 space-y-3">
                <div className="text-[13px] font-semibold">{t("integration.wp.shortcodes", "Shortcode-t")}</div>
                <div>
                    <div className="mb-1 flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Redirect mode</span>
                        <CopyButton value={shortcodeRedirect} label={t("integration.copy", "Kopjo")} />
                    </div>
                    <code className="block rounded-md border border-border bg-muted/30 p-2 font-mono text-[11px]" data-testid="wp-shortcode-redirect">{shortcodeRedirect}</code>
                </div>
                <div>
                    <div className="mb-1 flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Widget overlay mode</span>
                        <CopyButton value={shortcodeWidget} label={t("integration.copy", "Kopjo")} />
                    </div>
                    <code className="block rounded-md border border-border bg-muted/30 p-2 font-mono text-[11px]" data-testid="wp-shortcode-widget">{shortcodeWidget}</code>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                    {t("integration.wp.attrs", "Atributet e disponueshme: property, key, mode (redirect/widget), layout (horizontal/vertical/floating), theme (light/dark), language (sq/en), color.")}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// API Access (keys + allowed origins + rate limit)
// ─────────────────────────────────────────────────────────────────────────
function ApiAccessTab({ cfg, reload, hotelId }) {
    const { t } = useTranslation();
    const [origins, setOrigins] = useState(cfg.allowed_origins.join("\n"));
    const [newKeyResult, setNewKeyResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const saveOrigins = async () => {
        setBusy(true);
        try {
            const list = origins.split("\n").map((s) => s.trim()).filter(Boolean);
            const { data } = await api.patch(`/properties/${hotelId}/integration/allowed-origins`, { allowed_origins: list });
            setOrigins(data.allowed_origins.join("\n"));
            toast.success(t("integration.api.origins_saved", "Origjinat u ruajtën"));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const rotatePublishable = async () => {
        if (!confirm(t("integration.api.confirm_rotate_pub", "Rotate publishable key? Çelësi i vjetër do të ndalojë të funksionojë menjëherë."))) return;
        try {
            await api.post(`/properties/${hotelId}/integration/rotate-publishable`);
            toast.success(t("integration.api.pub_rotated", "Publishable key u rotua"));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const createSecret = async () => {
        try {
            const { data } = await api.post(`/properties/${hotelId}/integration/secret-keys`, { name: prompt("Emri i çelësit?", "Server key") || "Server key" });
            setNewKeyResult(data.secret_key);
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const revokeSecret = async (keyId) => {
        if (!confirm(t("integration.api.confirm_revoke", "Revoke këtë secret key?"))) return;
        try {
            await api.delete(`/properties/${hotelId}/integration/secret-keys/${keyId}`);
            toast.success(t("integration.api.revoked", "U revokua"));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4" data-testid="integration-api">
            {/* Publishable */}
            <div className="akm-card p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[13px] font-semibold">Publishable key</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {t("integration.api.pub_hint", "I sigurt për t'u vendosur në kod të frontend-it (widget, WordPress plugin, iOS/Android app).")}
                        </div>
                    </div>
                    <button onClick={rotatePublishable} className="akm-btn-ghost text-[11px]" data-testid="rotate-pub">
                        <RefreshCw size={11} /> {t("integration.api.rotate", "Rotate")}
                    </button>
                </div>
                <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-muted/30 p-2.5 font-mono text-[12px]">
                    <span className="flex-1 truncate" data-testid="publishable-key-value">{cfg.publishable_key}</span>
                    <CopyButton value={cfg.publishable_key} label={t("integration.copy", "Kopjo")} />
                </div>
            </div>

            {/* Secret keys */}
            <div className="akm-card p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[13px] font-semibold">Secret keys</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {t("integration.api.sec_hint", "Vetëm për backend / server-to-server. Deri në 2 çelësa aktivë njëkohësisht për zero-downtime rotation.")}
                        </div>
                    </div>
                    <button onClick={createSecret} className="akm-btn-primary text-[11px]" data-testid="create-secret" disabled={cfg.secret_keys.filter((k) => !k.revoked_at).length >= 2}>
                        <Plus size={11} /> {t("integration.api.new_secret", "Krijo çelës të ri")}
                    </button>
                </div>
                {newKeyResult && (
                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
                        <div className="flex items-start gap-2">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-700" />
                            <div className="flex-1">
                                <div className="text-[12px] font-semibold text-amber-900">
                                    {t("integration.api.sec_once", "Ky është shfaqja e vetme e këtij çelësi. Ruaje diku të sigurt.")}
                                </div>
                                <div className="mt-1 flex items-center gap-2 rounded-md border border-amber-300 bg-white p-2 font-mono text-[11px]" data-testid="new-secret-value">
                                    <span className="flex-1 break-all">{newKeyResult}</span>
                                    <CopyButton value={newKeyResult} label={t("integration.copy", "Kopjo")} />
                                </div>
                                <button onClick={() => setNewKeyResult(null)} className="mt-2 text-[11px] text-amber-800 hover:underline">
                                    {t("integration.api.sec_seen", "E ruajta — mbylle")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-3 space-y-1">
                    {cfg.secret_keys.length === 0 && (
                        <div className="py-3 text-center text-[11px] text-muted-foreground">
                            {t("integration.api.no_secrets", "Ende s'ka çelësa server-to-server.")}
                        </div>
                    )}
                    {cfg.secret_keys.map((k) => (
                        <div key={k.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-[12px]" data-testid={`secret-${k.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium">{k.name}</div>
                                <div className="text-[10px] text-muted-foreground font-mono">{k.display} · created {k.created_at?.slice(0, 10)}</div>
                            </div>
                            {k.revoked_at ? (
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px]">Revoked</span>
                            ) : (
                                <button onClick={() => revokeSecret(k.id)} className="akm-btn-ghost text-[10px] text-red-600" data-testid={`revoke-${k.id}`}>
                                    <Trash2 size={11} /> Revoke
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Allowed origins */}
            <div className="akm-card p-4">
                <div className="text-[13px] font-semibold">{t("integration.api.origins", "Allowed origins (CORS)")}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {t("integration.api.origins_hint", "Një origin për rresht. Bosh = pranohet çdo faqe (jo i rekomanduar në prodhim).")}
                </div>
                <textarea rows="4"
                          value={origins}
                          onChange={(e) => setOrigins(e.target.value)}
                          className="akm-input mt-2 font-mono text-[12px]"
                          placeholder="https://holidayhotel.al&#10;https://www.holidayhotel.al"
                          data-testid="allowed-origins" />
                <div className="mt-2 flex justify-end">
                    <button onClick={saveOrigins} disabled={busy} className="akm-btn-primary text-[11px]" data-testid="save-origins">
                        {busy ? t("common.saving") : t("integration.api.save_origins", "Ruaj listën")}
                    </button>
                </div>
            </div>

            {/* Rate limit — read-only for hotel admin */}
            <div className="akm-card p-4">
                <div className="text-[13px] font-semibold flex items-center gap-2">
                    <ShieldCheck size={13} /> {t("integration.api.rate_limit", "Rate limit")}
                </div>
                <div className="mt-1 text-[12px] text-muted-foreground">
                    {t("integration.api.rate_current", "Aktualisht")}: <strong>{cfg.rate_limit_per_min} kërkesa / minutë</strong> per property. {t("integration.api.rate_hint", "Modifikohet vetëm nga Super Admin.")}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Analytics
// ─────────────────────────────────────────────────────────────────────────
function AnalyticsTab({ analytics, onRefresh }) {
    const { t } = useTranslation();
    if (!analytics) return <div className="p-6 text-sm text-muted-foreground">{t("common.loading_ellipsis")}</div>;
    const kpis = [
        ["Widget loads",  analytics.widget_loads],
        ["Config calls",  analytics.config_requests],
        ["Availability", analytics.availability],
        ["Booking holds", analytics.holds],
        ["Checkouts",     analytics.checkouts],
        ["Reservations",  analytics.reservations],
    ];
    return (
        <div className="space-y-4" data-testid="integration-analytics">
            <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold">{t("integration.analytics.window", "30 ditët e fundit")}</div>
                <button onClick={onRefresh} className="akm-btn-ghost text-[11px]" data-testid="analytics-refresh">
                    <RefreshCw size={11} /> {t("common.refresh", "Rifresko")}
                </button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                {kpis.map(([label, v]) => (
                    <div key={label} className="akm-card p-4">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                        <div className="mt-1 text-2xl font-semibold tabular-nums">{v || 0}</div>
                    </div>
                ))}
            </div>
            {analytics.last_activity ? (
                <div className="akm-card p-4 text-[12px] text-muted-foreground">
                    {t("integration.analytics.last", "Aktiviteti i fundit")}: <strong>{analytics.last_activity.event}</strong> · {new Date(analytics.last_activity.ts).toLocaleString()}
                </div>
            ) : (
                <div className="akm-card p-4 text-[12px] text-muted-foreground">
                    {t("integration.analytics.empty", "Ende s'ka aktivitet nga API publike apo widget.")}
                </div>
            )}
        </div>
    );
}

function FormRow({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Domains — custom domain onboarding with CNAME + TXT verification
// ─────────────────────────────────────────────────────────────────────────
function DomainsTab({ cfg, reload, hotelId }) {
    const { t } = useTranslation();
    const [newDomain, setNewDomain] = useState("");
    const [busy, setBusy] = useState(false);
    const add = async () => {
        if (!newDomain.trim()) return;
        setBusy(true);
        try {
            await api.post(`/properties/${hotelId}/integration/domains`, { domain: newDomain.trim() });
            setNewDomain("");
            reload();
            toast.success(t("integration.domains.added", "Domain u shtua — konfiguro DNS-në dhe verifikoje"));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const verify = async (dom) => {
        try {
            const { data } = await api.post(`/properties/${hotelId}/integration/domains/${dom.id}/verify`);
            if (data.status === "verified") toast.success(t("integration.domains.verified", "Domain-i u verifikua"));
            else toast.error((t("integration.domains.failed", "Verifikimi dështoi") + ": ") + (data.last_check_error || ""));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const del = async (dom) => {
        if (!confirm(t("integration.domains.confirm_delete", "Të fshihet domain-i?"))) return;
        await api.delete(`/properties/${hotelId}/integration/domains/${dom.id}`);
        reload();
    };
    return (
        <div className="space-y-4" data-testid="integration-domains">
            <div className="akm-card p-4">
                <div className="text-[13px] font-semibold">{t("integration.domains.title", "Shto domain të personalizuar")}</div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                    {t("integration.domains.hint", "P.sh. booking.holidayhotel.al. Pas verifikimit, klientët mund të vizitojnë atë URL direkt për të rezervuar.")}
                </div>
                <div className="mt-2 flex gap-2">
                    <input className="akm-input font-mono" placeholder="booking.hotelijuaj.al"
                           value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
                           data-testid="new-domain-input" />
                    <button onClick={add} disabled={busy} className="akm-btn-primary" data-testid="add-domain">
                        <Plus size={12} /> {t("integration.domains.add", "Shto")}
                    </button>
                </div>
            </div>

            {cfg.custom_domains.length === 0 && (
                <div className="rounded-md border border-dashed border-border bg-card/50 py-10 text-center text-sm text-muted-foreground">
                    {t("integration.domains.empty", "Ende s'ka domain të personalizuar.")}
                </div>
            )}

            {cfg.custom_domains.map((d) => (
                <div key={d.id} className="akm-card p-4" data-testid={`domain-${d.id}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-[13px] font-semibold">
                                {d.domain}
                                <span className={`rounded-full px-2 py-0 text-[10px] font-medium ${
                                    d.status === "verified" ? "bg-emerald-50 text-emerald-700" :
                                    d.status === "failed" ? "bg-red-50 text-red-700" :
                                    "bg-amber-50 text-amber-800"
                                }`}>{d.status}</span>
                            </div>
                            {d.verified_at && <div className="text-[10px] text-muted-foreground">Verified {new Date(d.verified_at).toLocaleString()}</div>}
                            {d.last_check_error && (
                                <div className="mt-1 text-[11px] text-red-700">
                                    <XCircle size={10} className="mr-1 inline" /> {d.last_check_error}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => verify(d)} className="akm-btn-primary text-[11px]" data-testid={`verify-${d.id}`}>
                                <RefreshCw size={11} /> {t("integration.domains.verify", "Verifiko")}
                            </button>
                            <button onClick={() => del(d)} className="akm-btn-ghost text-[11px] text-red-600" data-testid={`delete-domain-${d.id}`}>
                                <Trash2 size={11} />
                            </button>
                        </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-[11px]">
                        <div className="font-semibold mb-2">{t("integration.domains.dns_setup", "Konfigurimi DNS")} — 2 rekorde:</div>
                        <div className="space-y-2 font-mono">
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                                <span className="text-muted-foreground">Type</span><span>CNAME</span>
                                <span className="text-muted-foreground">Name</span><span>{d.domain}</span>
                                <span className="text-muted-foreground">Value</span><span>app.akomoda.co</span>
                            </div>
                            <div className="mt-2 grid grid-cols-[80px_1fr] gap-2 border-t border-border pt-2">
                                <span className="text-muted-foreground">Type</span><span>TXT</span>
                                <span className="text-muted-foreground">Name</span><span>_akomoda-challenge.{d.domain}</span>
                                <span className="text-muted-foreground">Value</span>
                                <span className="flex items-center gap-2">
                                    <span>{d.verification_token}</span>
                                    <CopyButton value={d.verification_token} />
                                </span>
                            </div>
                        </div>
                        <div className="mt-2 text-[10px] text-muted-foreground">
                            {t("integration.domains.dns_time", "Ndryshimet DNS mund të marrin 5-60 minuta për t'u përhapur. Kliko \"Verifiko\" kur të jenë gati.")}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Branding — logo, colors, background, welcome message
// ─────────────────────────────────────────────────────────────────────────
function BrandingTab({ cfg, reload, hotelId }) {
    const { t } = useTranslation();
    const [b, setB] = useState(cfg.branding || {});
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/properties/${hotelId}/integration/branding`, b);
            toast.success(t("integration.branding.saved", "Branding u ruajt"));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="integration-branding">
            <div className="akm-card p-4 space-y-3 lg:col-span-1">
                <div className="text-[13px] font-semibold">{t("integration.branding.title", "Branding")}</div>
                <FormRow label={t("integration.branding.logo", "URL i logos")}>
                    <input className="akm-input font-mono" value={b.logo_url || ""} onChange={(e) => setB({ ...b, logo_url: e.target.value })}
                           placeholder="https://holidayhotel.al/logo.png" data-testid="branding-logo" />
                </FormRow>
                <FormRow label={t("integration.branding.primary", "Ngjyra primare")}>
                    <div className="flex items-center gap-2">
                        <input type="color" value={b.primary_color || "#ff7b02"} onChange={(e) => setB({ ...b, primary_color: e.target.value })}
                               className="h-8 w-14 rounded border border-border" data-testid="branding-primary" />
                        <input className="akm-input font-mono" value={b.primary_color || ""} onChange={(e) => setB({ ...b, primary_color: e.target.value })} />
                    </div>
                </FormRow>
                <FormRow label={t("integration.branding.secondary", "Ngjyra dytësore")}>
                    <div className="flex items-center gap-2">
                        <input type="color" value={b.secondary_color || "#0F172A"} onChange={(e) => setB({ ...b, secondary_color: e.target.value })}
                               className="h-8 w-14 rounded border border-border" />
                        <input className="akm-input font-mono" value={b.secondary_color || ""} onChange={(e) => setB({ ...b, secondary_color: e.target.value })} />
                    </div>
                </FormRow>
                <FormRow label={t("integration.branding.background", "URL i background-it")}>
                    <input className="akm-input font-mono" value={b.background_url || ""} onChange={(e) => setB({ ...b, background_url: e.target.value })}
                           placeholder="https://holidayhotel.al/hero.jpg" />
                </FormRow>
                <FormRow label={t("integration.branding.welcome", "Mesazhi i mirëseardhjes")}>
                    <textarea rows="2" className="akm-input" value={b.welcome_message || ""} onChange={(e) => setB({ ...b, welcome_message: e.target.value })}
                              placeholder="Mirësevini në Holiday Hotel — 5 min nga aeroporti." />
                </FormRow>
                <FormRow label={t("integration.branding.font", "Font family")}>
                    <select className="akm-input" value={b.font_family || "Inter"} onChange={(e) => setB({ ...b, font_family: e.target.value })}>
                        {["Inter", "Roboto", "Poppins", "Open Sans", "Lato", "Playfair Display", "Merriweather"].map((f) =>
                            <option key={f} value={f}>{f}</option>)}
                    </select>
                </FormRow>
                <button onClick={save} disabled={saving} className="akm-btn-primary w-full" data-testid="branding-save">
                    {saving ? t("common.saving") : t("integration.branding.save", "Ruaj brandin")}
                </button>
            </div>

            <div className="lg:col-span-2">
                <div className="akm-card overflow-hidden">
                    <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("integration.branding.preview", "Parapamje")}
                    </div>
                    <div className="relative min-h-[360px]"
                         style={{ backgroundColor: b.secondary_color || "#0F172A",
                                  backgroundImage: b.background_url ? `url(${b.background_url})` : undefined,
                                  backgroundSize: "cover", backgroundPosition: "center" }}>
                        <div className="absolute inset-0 bg-black/40" />
                        <div className="relative flex flex-col items-center justify-center gap-4 p-8 text-center text-white">
                            {b.logo_url ? <img src={b.logo_url} alt="Logo" className="max-h-16" /> : <div className="rounded bg-white/10 px-4 py-2 text-xs">Logo</div>}
                            <h2 style={{ fontFamily: b.font_family || "Inter" }} className="text-3xl font-semibold">{cfg.hotel.name}</h2>
                            {b.welcome_message && <p className="max-w-md text-sm opacity-90">{b.welcome_message}</p>}
                            <button style={{ backgroundColor: b.primary_color || "#ff7b02" }}
                                    className="rounded-md px-5 py-2 font-semibold text-white shadow-lg">
                                Rezervo dhomën
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Payments — hotel's own Stripe account (BYOK)
// ─────────────────────────────────────────────────────────────────────────
function PaymentsTab({ cfg, reload, hotelId }) {
    const { t } = useTranslation();
    const s = cfg.stripe || {};
    const [form, setForm] = useState({ mode: "test", publishable_key: "", secret_key: "", webhook_secret: "" });
    const [busy, setBusy] = useState(false);
    const [whSecret, setWhSecret] = useState("");
    const [whBusy, setWhBusy] = useState(false);

    const webhookUrl = useMemo(() => {
        const apiBase = (cfg.hosted_booking_url || "").split("/booking/")[0]
            || (process.env.REACT_APP_BACKEND_URL || "");
        const pid = cfg.public_property_id || "";
        return apiBase && pid ? `${apiBase.replace(/\/$/, "")}/api/v1/public/stripe/webhook/${pid}` : "";
    }, [cfg]);

    const connect = async () => {
        setBusy(true);
        try {
            const payload = { mode: form.mode, publishable_key: form.publishable_key, secret_key: form.secret_key };
            if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;
            await api.put(`/properties/${hotelId}/integration/stripe`, payload);
            toast.success(t("integration.payments.connected", "Stripe u lidh me sukses"));
            setForm({ mode: "test", publishable_key: "", secret_key: "", webhook_secret: "" });
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const disconnect = async () => {
        if (!confirm(t("integration.payments.confirm_disconnect", "Të shkëputet lidhja me Stripe? Rezervimet e reja nuk do të pranojnë pagesa me kartë."))) return;
        try {
            await api.delete(`/properties/${hotelId}/integration/stripe`);
            toast.success(t("integration.payments.disconnected", "Stripe u shkëput"));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const saveWebhookSecret = async () => {
        setWhBusy(true);
        try {
            await api.patch(`/properties/${hotelId}/integration/stripe/webhook-secret`, { webhook_secret: whSecret });
            toast.success(t("integration.payments.webhook_saved", "Sekreti i webhook-ut u ruajt"));
            setWhSecret("");
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setWhBusy(false); }
    };
    const clearWebhookSecret = async () => {
        if (!confirm(t("integration.payments.confirm_clear_webhook", "Të fshihet sekreti i webhook-ut? Callback-et pa nënshkrim do të tolerohen përsëri."))) return;
        setWhBusy(true);
        try {
            await api.patch(`/properties/${hotelId}/integration/stripe/webhook-secret`, { webhook_secret: "" });
            toast.success(t("integration.payments.webhook_cleared", "Sekreti u fshi"));
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setWhBusy(false); }
    };
    const copyWebhookUrl = () => {
        if (!webhookUrl) return;
        navigator.clipboard.writeText(webhookUrl);
        toast.success(t("common.copied", "U kopjua"));
    };
    return (
        <div className="space-y-4" data-testid="integration-payments">
            <div className="akm-card p-4 bg-blue-50/40 border-blue-200">
                <div className="flex items-start gap-2">
                    <Info size={16} className="mt-0.5 text-blue-700" />
                    <div className="text-[12px] text-blue-900">
                        <div className="font-semibold">{t("integration.payments.own_account", "Lidhet llogaria juaj e Stripe-it, jo e Akomoda-s.")}</div>
                        <div className="mt-1">
                            {t("integration.payments.explainer", "Pagesat nga klientët shkojnë direkt në llogarinë tuaj bankare përmes Stripe-it. Akomoda vetëm ndihmon në krijimin e sesionit — nuk merr komisione mbi transaksionet.")}
                        </div>
                        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer"
                           className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium underline">
                            <ExternalLink size={11} /> {t("integration.payments.get_keys", "Merr çelësat nga Stripe Dashboard →")}
                        </a>
                    </div>
                </div>
            </div>

            {s.connected ? (
                <>
                <div className="akm-card p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <div className="text-[14px] font-semibold">{t("integration.payments.status_connected", "Lidhur me Stripe")}</div>
                        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${s.mode === "live" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                            {s.mode === "live" ? "LIVE" : "TEST"}
                        </span>
                    </div>
                    <dl className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        <KVRow k={t("integration.payments.account", "Llogaria")} v={s.account_display_name || "(pa emër)"} />
                        <KVRow k={t("integration.payments.email", "Email")} v={s.account_email || "—"} />
                        <KVRow k={t("integration.payments.country", "Vendi")} v={s.account_country || "—"} />
                        <KVRow k="Publishable key" v={s.publishable_key} />
                        <KVRow k="Secret key" v={"sk_" + s.mode + "_…" + s.secret_last4} />
                        <KVRow k={t("integration.payments.connected_at", "Lidhur më")} v={s.connected_at ? new Date(s.connected_at).toLocaleString() : "—"} />
                    </dl>
                    <div className="mt-3 flex justify-end">
                        <button onClick={disconnect} className="akm-btn-ghost text-[11px] text-red-600" data-testid="stripe-disconnect">
                            <Trash2 size={11} /> {t("integration.payments.disconnect", "Shkëput")}
                        </button>
                    </div>
                </div>

                {/* Webhook Secret card */}
                <div className="akm-card p-4" data-testid="stripe-webhook-card">
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck size={16} className={s.webhook_secret_set ? "text-emerald-600" : "text-amber-500"} />
                        <div className="text-[14px] font-semibold">
                            {t("integration.payments.webhook_title", "Webhook Signing Secret")}
                        </div>
                        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${s.webhook_secret_set ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                            {s.webhook_secret_set ? t("integration.payments.webhook_configured", "I konfiguruar") : t("integration.payments.webhook_missing", "I paverifikuar")}
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-3">
                        {t("integration.payments.webhook_help", "Për të verifikuar automatikisht që pagesa u krye, shto këtë URL si endpoint webhook në panelin tuaj të Stripe-it dhe ngjit sekretin e nënshkrimit (whsec_…) këtu poshtë. Pa këtë, verifikimi bëhet nga vetë shfletuesi i klientit (dyfish i sigurt).")}
                    </div>

                    {/* Webhook URL */}
                    <div className="mb-3">
                        <div className="text-[11px] font-medium mb-1">
                            {t("integration.payments.webhook_url", "URL-ja e webhook-ut (ngjite në Stripe Dashboard)")}
                        </div>
                        <div className="flex items-center gap-2">
                            <input readOnly value={webhookUrl} className="akm-input font-mono text-[11px]"
                                   data-testid="stripe-webhook-url" />
                            <button onClick={copyWebhookUrl} className="akm-btn-ghost text-[11px]"
                                    data-testid="stripe-webhook-copy">
                                <Copy size={12} /> {t("common.copy", "Kopjo")}
                            </button>
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground">
                            {t("integration.payments.webhook_event", "Ngjarjet e nevojshme: ")}
                            <code className="rounded bg-muted px-1 py-0.5">checkout.session.completed</code>
                        </div>
                    </div>

                    {/* Current status */}
                    {s.webhook_secret_set && (
                        <dl className="grid grid-cols-1 gap-2 md:grid-cols-2 mb-3">
                            <KVRow k={t("integration.payments.webhook_current", "Sekreti aktual")}
                                   v={"whsec_…" + (s.webhook_secret_last4 || "····")} />
                            <KVRow k={t("integration.payments.webhook_updated", "Përditësuar më")}
                                   v={s.webhook_updated_at ? new Date(s.webhook_updated_at).toLocaleString() : "—"} />
                        </dl>
                    )}

                    {/* Set / rotate */}
                    <FormRow label={t("integration.payments.webhook_set_label", s.webhook_secret_set ? "Rrotullo (paste new whsec_…)" : "Sekreti i nënshkrimit (whsec_…)")}>
                        <div className="flex items-center gap-2">
                            <input type="password" placeholder="whsec_…"
                                   className="akm-input font-mono"
                                   value={whSecret}
                                   onChange={(e) => setWhSecret(e.target.value)}
                                   data-testid="stripe-webhook-input" />
                            <button onClick={saveWebhookSecret}
                                    disabled={whBusy || !whSecret || !whSecret.startsWith("whsec_")}
                                    className="akm-btn-primary text-[11px]"
                                    data-testid="stripe-webhook-save">
                                {whBusy ? t("integration.payments.saving", "Duke ruajtur…") : t("common.save", "Ruaj")}
                            </button>
                            {s.webhook_secret_set && (
                                <button onClick={clearWebhookSecret} className="akm-btn-ghost text-[11px] text-red-600"
                                        data-testid="stripe-webhook-clear">
                                    <Trash2 size={11} />
                                </button>
                            )}
                        </div>
                    </FormRow>
                </div>
                </>
            ) : (
                <div className="akm-card p-4">
                    <div className="text-[13px] font-semibold mb-2">{t("integration.payments.connect", "Lidh Stripe-in")}</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <FormRow label={t("integration.payments.mode", "Mode")}>
                            <select className="akm-input" value={form.mode}
                                    onChange={(e) => setForm({ ...form, mode: e.target.value })}
                                    data-testid="stripe-mode">
                                <option value="test">Test (sk_test_… / pk_test_…)</option>
                                <option value="live">Live (sk_live_… / pk_live_…)</option>
                            </select>
                        </FormRow>
                        <FormRow label="Publishable key">
                            <input className="akm-input font-mono" placeholder={`pk_${form.mode}_…`} value={form.publishable_key}
                                   onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
                                   data-testid="stripe-pk" />
                        </FormRow>
                        <FormRow label="Secret key">
                            <input type="password" className="akm-input font-mono" placeholder={`sk_${form.mode}_…`} value={form.secret_key}
                                   onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
                                   data-testid="stripe-sk" />
                        </FormRow>
                    </div>
                    <div className="mt-3">
                        <FormRow label={t("integration.payments.webhook_optional", "Webhook signing secret (opsional — mund të shtohet edhe më vonë)")}>
                            <input type="password" className="akm-input font-mono" placeholder="whsec_…" value={form.webhook_secret}
                                   onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                                   data-testid="stripe-whsec" />
                        </FormRow>
                    </div>
                    <div className="mt-3 flex justify-end">
                        <button onClick={connect} disabled={busy || !form.publishable_key || !form.secret_key}
                                className="akm-btn-primary" data-testid="stripe-connect">
                            <CreditCard size={13} /> {busy ? t("integration.payments.testing", "Duke testuar…") : t("integration.payments.connect_btn", "Lidhu me Stripe")}
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-md border border-border bg-muted/20 p-3 text-[11px] text-muted-foreground">
                <strong>{t("integration.payments.how_it_works", "Si punon:")}</strong>{" "}
                {t("integration.payments.flow", "Kur një klient bën checkout në motorin publik, Akomoda krijon një Payment Intent me çelësin sekret të hotelit. Klienti paguan me kartë ose Apple Pay / Google Pay. Fondet shkojnë direkt në llogarinë e Stripe-it të hotelit. Akomoda nuk kërkon një llogari të veten Stripe për këtë flow.")}
            </div>
        </div>
    );
}

